import {Card, Tag, Tooltip, Alert, Progress, Typography} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  SafetyCertificateFilled,
} from '@ant-design/icons';
import {checklistProgress, resolveRequirementDocuments} from '../kybUtils';
import styles from '../kybDashboard.module.scss';

const {Text} = Typography;

const CONDITION_HINTS = {
  IF_CONTACT_NOT_OWNER:
    'Required because the contact acts under power of attorney rather than as an owner.',
  PER_OWNER: 'One row per beneficial owner.',
};

/**
 * The compliance checklist, rendered from `kybRequirements` alone.
 *
 * The requirement set is config in the database, resolved per account against
 * its authority basis and owner list, so it changes per country without a
 * deploy — hardcoding a document list here would quietly go stale. It is also
 * the same shape the business sees on its own Verification tab, which is the
 * point: reviewer and customer read one checklist and cannot disagree about
 * what is outstanding.
 */
export const KybRequirementsChecklist = ({requirements, documents}) => {
  const rows = requirements || [];
  const progress = checklistProgress(rows);
  const percent = progress.mandatoryTotal
    ? Math.round((progress.mandatorySatisfied / progress.mandatoryTotal) * 100)
    : 0;

  return (
    <Card
      title="Requirement checklist"
      className={styles.card}
      extra={
        rows.length ? (
          <Text type="secondary">
            {progress.mandatorySatisfied} of {progress.mandatoryTotal} mandatory
            {progress.optionalTotal
              ? ` · ${progress.optionalSatisfied}/${progress.optionalTotal} optional`
              : ''}
          </Text>
        ) : null
      }>
      {!rows.length ? (
        // An empty checklist is not a satisfied one. It almost always means
        // KybRequirementConfig was never seeded in this environment, which
        // also lets a business submit with no documents at all.
        <Alert
          type="warning"
          showIcon
          message="No requirements are configured for this account."
          description="The requirement config resolves empty, so nothing is being asked of this business. Check that the KYB requirement seed has been run in this environment before reviewing."
        />
      ) : (
        <>
          <Progress
            percent={percent}
            strokeColor="#8d6cfa"
            className={styles.checklistProgress}
          />
          <ul className={styles.checklist}>
            {rows.map(row => {
              const proof = resolveRequirementDocuments(row, documents);
              return (
                <li key={row.key} className={styles.checklistRow}>
                  <span className={styles.checklistIcon}>
                    {row.satisfiedByContactKyc ? (
                      <SafetyCertificateFilled style={{color: '#8d6cfa'}} />
                    ) : row.satisfied ? (
                      <CheckCircleFilled style={{color: '#39b54a'}} />
                    ) : (
                      <CloseCircleFilled
                        style={{color: row.mandatory ? '#e5484d' : '#c9c4d8'}}
                      />
                    )}
                  </span>

                  <div className={styles.checklistBody}>
                    <div className={styles.checklistLabel}>
                      <span>{row.label || row.documentType || row.key}</span>
                      {!row.mandatory && <Tag>Optional</Tag>}
                      {CONDITION_HINTS[row.condition] && (
                        <Tooltip title={CONDITION_HINTS[row.condition]}>
                          <Tag color="purple">
                            {row.condition === 'PER_OWNER'
                              ? 'Per owner'
                              : 'Conditional'}
                          </Tag>
                        </Tooltip>
                      )}
                    </div>

                    {row.satisfiedByContactKyc ? (
                      // There is no file to review here: this owner is the
                      // account administrator and their identity was already
                      // proven by the individual KYC pipeline.
                      <Text type="secondary">
                        Verified via KYC — no document to review.
                      </Text>
                    ) : proof.length ? (
                      <Text type="secondary">
                        {proof
                          .map(
                            doc =>
                              `${doc.freeName || doc.fileName || 'File'} (${(
                                doc.status || 'PENDING'
                              ).toLowerCase()})`,
                          )
                          .join(', ')}
                      </Text>
                    ) : (
                      <Text type="secondary">
                        {row.satisfied
                          ? 'Satisfied.'
                          : 'Nothing uploaded for this requirement yet.'}
                      </Text>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
};
